import {getTenantDomain} from 'common/HelperFunctions'
import {putToSignedUrl} from 'common/helpers/signedUpload'
import {toast} from 'react-toastify'
import {confirmFileUpload, getSignedUploadUrl} from 'setup/redux/Actions/Site/site'
import store from 'setup/redux/Store'

// The backend's signed-url endpoint whitelists these image content types
// (IMAGE_CONTENT_TYPES in ilmversity-node); anything else is rejected with a
// validation error, so the picker must not offer wider `image/*`.
const SUPPORTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif']

/**
 * Uploads an editor image to tenant storage via the signed-URL flow and
 * resolves with the public file URL. Shared by every rich-text editor
 * instance (previously duplicated across the two Quill wrappers).
 */
export const uploadRichTextImage = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (!SUPPORTED_IMAGE_TYPES.includes(file.type)) {
      toast.error('Unsupported image type — please use a JPG, PNG or GIF file')
      reject(new Error(`Unsupported image type: ${file.type}`))
      return
    }
    const domain = getTenantDomain()
    const tenant = domain.replaceAll('.', '_')
    const IMAGE_UPLOAD_PATH = `uploads/${tenant}/images`

    const lastIndex = file.name.lastIndexOf('.')
    const fileName = lastIndex > 0 ? file.name.slice(0, lastIndex) : file.name
    const body = {
      path: IMAGE_UPLOAD_PATH,
      fileName,
      contentType: file.type,
    }

    const onFailure = (err: any) => {
      const message = err?.response?.data?.message ?? err?.message ?? 'Error in uploading the file'
      toast.error(message)
      reject(err)
    }

    store.dispatch(
      getSignedUploadUrl(
        body,
        (res: any) => {
          const {fileUrl, uploadURL} = res.result

          file.arrayBuffer().then((buffer: ArrayBuffer) => {
            toast.info(`Uploading ${file.name}`)
            putToSignedUrl(uploadURL, buffer, file.type)
              .then(() => {
                const confirmBody = {
                  path: IMAGE_UPLOAD_PATH,
                  original_filename: file.name,
                  file_name: fileUrl.split('/').slice(-1).pop(),
                  url: fileUrl,
                  size: file.size,
                  contentType: file.type,
                }

                store.dispatch(
                  confirmFileUpload(
                    confirmBody,
                    () => {
                      toast.success(`${file.name} uploaded successfully`)
                      resolve(fileUrl)
                    },
                    onFailure
                  )
                )
              })
              .catch(onFailure)
          })
        },
        onFailure
      )
    )
  })
}

/** Opens a file picker and resolves with the chosen image file (or null). */
export const pickImageFile = (): Promise<File | null> => {
  return new Promise((resolve) => {
    const input = document.createElement('input')
    input.setAttribute('type', 'file')
    input.setAttribute('accept', SUPPORTED_IMAGE_TYPES.join(','))
    input.onchange = () => resolve(input.files?.[0] ?? null)
    input.click()
  })
}
